import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { MONTHS_FR } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Download,
  FolderDown,
  Search,
  Calendar,
  FileSpreadsheet,
  CheckCircle2,
  FolderOpen,
  Archive,
  X,
  Loader2
} from 'lucide-react';

// --- TYPES ---
interface ArchivedSheet {
  id: string;
  year: number;
  week_number: number;
  status: string;
  pdf_url: string | null;
  signed_at: string;
  employee: { full_name: string } | null;
}

type FilterMode = 'week' | 'month' | 'year';

// --- CONSTANTES ---

// --- UTILITAIRES DE DATE ---
const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getWeekStartDate = (year: number, week: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

const getWeekEndDate = (weekStart: Date): Date => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};

export default function SecretariatDocuments() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ArchivedSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // États des filtres de date
  const [filterMode, setFilterMode] = useState<FilterMode>('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(getISOWeekNumber(new Date()));
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0 });

  // --- Calcul des dates de début/fin selon le mode ---
  const { periodLabel, dbPeriodStr } = useMemo(() => {
    let label: string, dbStr: string;

    if (filterMode === 'week') {
      const start = getWeekStartDate(selectedYear, selectedWeek);
      const end = getWeekEndDate(start);
      label = `Semaine ${selectedWeek} (${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })})`;
      dbStr = `S${selectedWeek}_${selectedYear}`;
    } else if (filterMode === 'month') {
      label = `${MONTHS_FR[selectedMonth]} ${selectedYear}`;
      dbStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
    } else {
      label = `Année ${selectedYear}`;
      dbStr = `${selectedYear}`;
    }

    return { periodLabel: label, dbPeriodStr: dbStr };
  }, [filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- Navigation temporelle ---
  const handlePrevious = () => {
    if (filterMode === 'week') {
      if (selectedWeek > 1) {
        setSelectedWeek(selectedWeek - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedWeek(52);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth > 0) {
        setSelectedMonth(selectedMonth - 1);
      } else {
        setSelectedYear(selectedYear - 1);
        setSelectedMonth(11);
      }
    } else {
      setSelectedYear(selectedYear - 1);
    }
  };

  const handleNext = () => {
    if (filterMode === 'week') {
      if (selectedWeek < 52) {
        setSelectedWeek(selectedWeek + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedWeek(1);
      }
    } else if (filterMode === 'month') {
      if (selectedMonth < 11) {
        setSelectedMonth(selectedMonth + 1);
      } else {
        setSelectedYear(selectedYear + 1);
        setSelectedMonth(0);
      }
    } else {
      setSelectedYear(selectedYear + 1);
    }
  };

  const handleToday = () => {
    const now = new Date();
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
    setSelectedWeek(getISOWeekNumber(now));
  };

  // --- Chargement des données ---
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('weekly_sheets')
        .select('*, employee:employees!employee_id(full_name)')
        .eq('status', 'validated')
        .order('year', { ascending: false })
        .order('week_number', { ascending: false });

      if (error) throw error;
      setDocuments(data as unknown as ArchivedSheet[]);
    } catch (err) {
      console.error("Erreur chargement archives:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // --- LOGIQUE DE FILTRAGE ---
  const filteredDocs = useMemo(() => {
    return documents.filter(doc => {
      // 1. Recherche par Nom
      const name = doc.employee?.full_name || '';
      const nameMatch = name.toLowerCase().includes(searchTerm.toLowerCase());

      // 2. Filtre selon le mode
      let periodMatch = true;
      if (filterMode === 'week') {
        periodMatch = doc.week_number === selectedWeek && doc.year === selectedYear;
      } else if (filterMode === 'month') {
        const weekStart = getWeekStartDate(doc.year, doc.week_number);
        periodMatch = weekStart.getMonth() === selectedMonth && weekStart.getFullYear() === selectedYear;
      } else {
        periodMatch = doc.year === selectedYear;
      }

      return nameMatch && periodMatch;
    });
  }, [documents, searchTerm, filterMode, selectedYear, selectedMonth, selectedWeek]);

  // --- EXPORT EXCEL ---
  const exportExcel = () => {
    const dataToExport = filteredDocs.map(doc => ({
      'Employe': doc.employee?.full_name || 'Inconnu',
      'Annee': doc.year,
      'Semaine': doc.week_number,
      'Date Validation': doc.signed_at ? new Date(doc.signed_at).toLocaleDateString('fr-FR') : 'N/A',
      'Lien PDF': doc.pdf_url || 'Non disponible'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Archives");

    worksheet['!cols'] = [
      { wch: 25 }, { wch: 10 }, { wch: 10 }, { wch: 20 }, { wch: 60 }
    ];

    const fileName = `archives_${dbPeriodStr}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // --- TÉLÉCHARGEMENT BATCH PDF (ZIP) ---
  const downloadAllPDFs = async () => {
    const sheetsWithPdf = filteredDocs.filter(doc => doc.pdf_url);
    if (sheetsWithPdf.length === 0) return;

    setZipDownloading(true);
    setZipProgress({ current: 0, total: sheetsWithPdf.length });

    try {
      const zip = new JSZip();
      const results = await Promise.allSettled(
        sheetsWithPdf.map(async (doc) => {
          const response = await fetch(doc.pdf_url!);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const empName = (doc.employee?.full_name || 'Inconnu')
            .replace(/[^a-zA-Z0-9À-ÿ\s_-]/g, '');
          const fileName = `${doc.year}_S${String(doc.week_number).padStart(2, '0')}_${empName}.pdf`;
          zip.file(fileName, blob);
          setZipProgress(prev => ({ ...prev, current: prev.current + 1 }));
        })
      );

      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) console.warn(`${failed} PDF(s) non téléchargés`);

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiches_validees_${dbPeriodStr}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement ZIP:', err);
    } finally {
      setZipDownloading(false);
      setZipProgress({ current: 0, total: 0 });
    }
  };

  // Statistiques
  const totalArchived = documents.length;
  const filteredCount = filteredDocs.length;
  const uniqueEmployees = new Set(filteredDocs.map(d => d.employee?.full_name)).size;

  const hasFilters = searchTerm !== '';

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* HEADER */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        padding: '16px 24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
              onClick={() => navigate('/secretariat')}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: '#f1f5f9', border: 'none', color: '#64748b',
                cursor: 'pointer', fontWeight: '500', padding: '8px 12px',
                borderRadius: '8px', fontSize: '0.85rem'
              }}
            >
              <ChevronLeft size={18} /> Retour
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ padding: '10px', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', borderRadius: '12px', color: 'white' }}>
                <Archive size={22} />
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Archives & Documents</h1>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.8rem' }}>Historique des feuilles validées</p>
              </div>
            </div>
          </div>

          {/* Boutons Export */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={downloadAllPDFs}
              disabled={zipDownloading || filteredDocs.filter(d => d.pdf_url).length === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: zipDownloading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                color: 'white', border: 'none', padding: '10px 20px',
                borderRadius: '10px',
                cursor: zipDownloading ? 'not-allowed' : 'pointer',
                fontWeight: '600', fontSize: '0.9rem',
                boxShadow: '0 4px 6px rgba(14, 165, 233, 0.3)',
                opacity: filteredDocs.filter(d => d.pdf_url).length === 0 ? 0.5 : 1
              }}
            >
              {zipDownloading ? <Loader2 size={18} className="spin" /> : <FolderDown size={18} />}
              {zipDownloading
                ? `${zipProgress.current}/${zipProgress.total} PDFs...`
                : `Télécharger tout (${filteredDocs.filter(d => d.pdf_url).length})`}
            </button>
            <button
              onClick={exportExcel}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white', border: 'none', padding: '10px 20px',
                borderRadius: '10px', cursor: 'pointer', fontWeight: '600',
                fontSize: '0.9rem', boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
              }}
            >
              <FileSpreadsheet size={18} /> Exporter Excel
            </button>
          </div>
        </div>
      </div>

      <div>

        {/* SECTION FILTRES + STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '20px', marginBottom: '24px' }}>

          {/* Carte Filtre de Date */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Période</h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Filtrer les archives par période</p>
            </div>

            {/* Sélecteur de mode */}
            <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '10px', marginBottom: '12px' }}>
              {(['week', 'month', 'year'] as FilterMode[]).map(mode => (
                <button
                  key={mode}
                  onClick={() => setFilterMode(mode)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.8rem',
                    background: filterMode === mode ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : 'transparent',
                    color: filterMode === mode ? 'white' : '#64748b',
                    transition: 'all 0.2s',
                    boxShadow: filterMode === mode ? '0 2px 4px rgba(14, 165, 233, 0.3)' : 'none'
                  }}
                >
                  {mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Année'}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: '#f8fafc', padding: '12px', borderRadius: '10px', marginBottom: '12px'
            }}>
              <button
                onClick={handlePrevious}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronLeft size={18} />
              </button>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Calendar size={16} color="#0ea5e9" />
                  <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>{periodLabel}</span>
                </div>
              </div>
              <button
                onClick={handleNext}
                style={{
                  padding: '8px', background: 'white', border: '1px solid #e2e8f0',
                  borderRadius: '8px', cursor: 'pointer', color: '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Sélecteurs détaillés */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px',
                  border: '1px solid #e2e8f0', fontSize: '0.85rem',
                  cursor: 'pointer', background: 'white'
                }}
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>

              {(filterMode === 'month' || filterMode === 'week') && (
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{
                    flex: 2, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {MONTHS_FR.map((m, idx) => (
                    <option key={idx} value={idx}>{m}</option>
                  ))}
                </select>
              )}

              {filterMode === 'week' && (
                <select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '0.85rem',
                    cursor: 'pointer', background: 'white'
                  }}
                >
                  {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                    <option key={w} value={w}>S{w}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Bouton Aujourd'hui */}
            <button
              onClick={handleToday}
              style={{
                width: '100%',
                padding: '10px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#15803d',
                fontWeight: '600',
                fontSize: '0.85rem',
                cursor: 'pointer'
              }}
            >
              Aujourd'hui
            </button>
          </div>

          {/* Cartes Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Documents sur période */}
            <div style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              borderRadius: '16px', padding: '20px', color: 'white',
              boxShadow: '0 4px 6px -1px rgba(14, 165, 233, 0.3)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', opacity: 0.9, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Documents
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                {loading ? '-' : filteredCount}
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>
                sur la période
              </div>
            </div>

            {/* Employés concernés */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Employés
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                {loading ? '-' : uniqueEmployees}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                collaborateur{uniqueEmployees > 1 ? 's' : ''}
              </div>
            </div>

            {/* Total archives */}
            <div style={{
              background: 'white', borderRadius: '16px', padding: '20px',
              border: '1px solid #bbf7d0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Total Archives
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1e293b' }}>
                {loading ? '-' : totalArchived}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                feuilles validées
              </div>
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '16px 20px',
          border: '1px solid #e2e8f0',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
        }}>
          <Search size={20} color="#94a3b8" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '0.95rem',
              color: '#1e293b',
              background: 'transparent'
            }}
          />
          {hasFilters && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                background: '#fee2e2', color: '#ef4444', border: 'none',
                padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: '600'
              }}
            >
              <X size={14} /> Effacer
            </button>
          )}
        </div>

        {/* GRILLE DES DOCUMENTS */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
              Feuilles archivées
            </h2>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {filteredCount} résultat{filteredCount > 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#0ea5e9" />
                Chargement des archives...
              </div>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#64748b' }}>
                <FolderOpen size={48} strokeWidth={1.5} />
                <div>
                  <p style={{ margin: 0, fontWeight: '600' }}>Aucun document trouvé</p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
                    Modifiez vos filtres pour voir d'autres résultats
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {filteredDocs.map(doc => {
                const empName = doc.employee?.full_name || 'Employe Inconnu';
                const initials = empName.split(' ').map(n => n[0]).join('').slice(0, 2);

                return (
                  <div
                    key={doc.id}
                    style={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '14px',
                      padding: '20px',
                      transition: 'all 0.2s',
                      cursor: 'default'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px -4px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.borderColor = '#0ea5e9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    {/* En-tête */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '12px',
                        background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700', color: '#0284c7', fontSize: '0.9rem'
                      }}>
                        {initials}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem', marginBottom: '2px' }}>
                          {empName}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#64748b' }}>
                          <Calendar size={12} />
                          S{doc.week_number} - {doc.year}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.75rem', color: '#15803d',
                        background: '#f0fdf4', padding: '4px 10px',
                        borderRadius: '20px', fontWeight: '500'
                      }}>
                        <CheckCircle2 size={12} />
                        {doc.signed_at ? new Date(doc.signed_at).toLocaleDateString('fr-FR') : 'N/A'}
                      </div>

                      {doc.pdf_url ? (
                        <a
                          href={doc.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white', borderRadius: '8px',
                            textDecoration: 'none', fontWeight: '600', fontSize: '0.8rem',
                            boxShadow: '0 2px 4px rgba(14, 165, 233, 0.3)',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                          <Download size={14} /> PDF
                        </a>
                      ) : (
                        <span style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          fontSize: '0.75rem', color: '#ef4444', fontStyle: 'italic'
                        }}>
                          <FileText size={12} /> Introuvable
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
